import { isSameMonth, parseISO } from "date-fns";
import { BarChart3, TriangleAlert } from "lucide-react";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import { CaptainUsageChart } from "./CaptainUsageChart";
import { CaptainUsageTable } from "./CaptainUsageTable";
import type { CaptainUsageRow } from "./CaptainUsageTooltip";
import { useCaptainBillingUsage } from "../../api/useCaptainBillingUsage";
import { CAPTAIN_USAGE_MONTHS } from "../../constants";
import { projectMonth } from "../../utils";

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/v2/card";
import EmptyState from "@/components/ui/v2/empty-state";
import { Skeleton } from "@/components/ui/v2/skeleton";
import { useIsMobile } from "@/hooks/use-mobile";
import { formatMonthYear } from "@/utils/dateUtils";

const MONTHS_NARROW = 6;

const SKELETON_HEIGHTS = [38, 52, 30, 64, 46, 72, 58, 80, 50, 68, 44, 34];

function UsageChartSkeleton({ bars }: { bars: number }) {
  return (
    <div className="flex h-[280px] items-end gap-3 pb-8 pl-10" data-testid="captain-usage-loading">
      {SKELETON_HEIGHTS.slice(-bars).map((height, index) => (
        <Skeleton key={index} className="flex-1 rounded-t-md rounded-b-none" style={{ height: `${height}%` }} />
      ))}
    </div>
  );
}

type CaptainUsageCardProps = {
  clientId: string;
  currentMonth?: string;
};

export function CaptainUsageCard({ clientId, currentMonth }: CaptainUsageCardProps) {
  const { t, i18n } = useTranslation();
  const isMobile = useIsMobile();
  const { data, isLoading, isError, refetch } = useCaptainBillingUsage(clientId, CAPTAIN_USAGE_MONTHS);
  const months = isMobile ? MONTHS_NARROW : CAPTAIN_USAGE_MONTHS;

  const rows = useMemo<CaptainUsageRow[]>(() => {
    const now = new Date();
    return (data ?? []).slice(-months).map((month) => {
      const used = month.usedFromCapTokens + month.usedFromTopUpTokens;
      const isCurrent = !!currentMonth && isSameMonth(parseISO(month.month), parseISO(currentMonth));
      return {
        month: month.month,
        label: formatMonthYear(parseISO(month.month), i18n.language, "MMM"),
        longLabel: formatMonthYear(parseISO(month.month), i18n.language),
        fromCap: month.usedFromCapTokens,
        fromTopUp: month.usedFromTopUpTokens,
        projected: isCurrent ? Math.max(0, projectMonth(used, month.month, now) - used) : 0,
        cap: month.capTokens,
        replies: month.replies,
        isCurrent,
      };
    });
  }, [data, months, currentMonth, i18n.language]);
  const hasUsage = rows.some((row) => row.replies > 0);

  return (
    <Card padding="md" className="h-full py-5">
      <CardHeader className="px-5">
        <CardTitle className="text-lg font-semibold text-foreground">{t("captainBilling.usage.title")}</CardTitle>
        <CardDescription>{t("captainBilling.usage.subtitle", { count: months })}</CardDescription>
      </CardHeader>
      <CardContent className="px-5">
        {isLoading && <UsageChartSkeleton bars={months} />}
        {!isLoading && isError && (
          <EmptyState
            icon={TriangleAlert}
            title={t("captainBilling.usage.errorTitle")}
            description={t("captainBilling.usage.errorDescription")}
            action={{ label: t("common.retry"), onClick: () => void refetch() }}
          />
        )}
        {!isLoading && !isError && !hasUsage && (
          <EmptyState
            icon={BarChart3}
            title={t("captainBilling.usage.emptyTitle")}
            description={t("captainBilling.usage.emptyDescription")}
          />
        )}
        {!isLoading && !isError && hasUsage && (
          <div className="space-y-4">
            <CaptainUsageChart rows={rows} />
            <CaptainUsageTable rows={rows} />
          </div>
        )}
      </CardContent>
    </Card>
  );
}
